<template>
  <validation-observer
    v-slot="additionalInsuredObserver"
    slim
  >
    <form-section
      :id="scrollTarget"
      ref="scrollTarget"
      v-bind="additionalInsuredObserver"
      :validated="formObserver.validated"
    >
      <!--  JOINT INSURED  -->
      <form-row
        label="Do you wish to have another person named on your insurance policy?"
        :error="formObserver.validated && !additionalInsuredObserver.valid"
      >
        <validation-provider
          v-slot="{ failed }"
          name="Joint Insured"
          rules="required"
          :vid="`${stepId}_joint-insured`"
          slim
        >
          <radio
            v-model="insured.jointInsured"
            name="joint-insured"
            :items="constants.YES_NO"
            :error="failed"
            wide
            data-test="joint-insured"
          />
        </validation-provider>
      </form-row>
      <help-text
        :error="formObserver.validated && !additionalInsuredObserver.valid"
      >
        <p>You may wish to add your partner or spouse as a joint policyholder.</p>
      </help-text>
      <template v-if="insured.jointInsured">
        <!--  SUMMARY -->
        <form-row
          v-if="!additionalInsured"
          class="additional"
          :wide="!$vuetify.breakpoint.smAndDown"
          :is-vertical-splitted="false"
        >
          <validation-provider
            v-slot="{ invalid }"
            vid="insured_additional-insured-relationship-to-proposer"
            :rules="{ is: { other: true } }"
            name="Additional Relationship"
            immediate
          >
            <input
              type="hidden"
              :value="isRelationshipProposerValid && isAllAdditionalInsuredItemsValid"
            >
            <div
              v-if="invalid"
              class="error--text mb-4 fz12"
            >
              Something doesn't look quite right here or there may be missing information. Please review details below:
            </div>
          </validation-provider>

          <box
            v-for="(item, index) in additionalInsuredList"
            :key="index"
            :class="{ 'mt-4': index > 0 }"
          >
            <icon
              slot="icon"
              size="56"
              :icon="helpers.getAdditionalIcon(item.additionalTitle)"
            />

            <ul class="mb-6">
              <li>
                <p><strong>{{ item.additionalTitle }} {{ item.additionalFirstName }} {{ item.additionalSurname }}</strong></p>
              </li>
              <li class="mr-6">
                <p>{{ item.additionalDateOfBirth }}</p>
              </li>
              <li>
                <p>
                  {{ constants.MARITAL_STATUS[item.additionalMaritalStatus] }},
                  <span
                    :class="{
                      'error--text': !isRelationshipProposerValid,
                    }"
                  >
                    {{ constants.INSURED_ADDITIONAL_RELATIONSHIP_PROPOSER[item.additionalRelationshipProposer] }}
                  </span>
                </p>
              </li>
              <li>{{ constants.INSURED_EMPLOYMENT_STATUS[item.additionalEmploymentStatus] }}</li>
              <li
                v-if="item.additionalOccupationKey"
              >
                {{ item.additionalOccupationKey }}
              </li>
              <li
                v-if="item.additionalBusinessKey"
              >
                {{ item.additionalBusinessKey }}
              </li>
              <li class="mr-6">Part time occupation: {{ item.additionalPartTimeOccupation ? 'Yes' : 'No' }}</li>
            </ul>
            <a
              slot="left-action"
              href="#"
              class="link-box"
              data-test="remove_additional_btn"
              @click.prevent="removeAdditionalInsured"
            >
              <span>Remove</span>
              <v-icon color="primary">mdi-delete</v-icon>
            </a>

            <a
              slot="right-action"
              href="#"
              class="link-box"
              data-test="change_these_details_additional_btn"
              @click.prevent="changeAdditionalInsured"
            >
              <span>
                <template v-if="!isRelationshipProposerValid || !isAllAdditionalInsuredItemsValid">
                  Fix
                </template>
                <template v-else>
                  Change
                </template>
                these details
              </span>
              <v-icon color="primary">mdi-pencil</v-icon>
            </a>
          </box>
        </form-row>

        <!-- ADDITIONAL INSURED FORM -->
        <form-additional-insured
          v-else
          :form-observer="formObserver"
          :form-data="formData"
          :insured="additionalInsured"
          :main-insured="insured"
          @save="saveAdditionalInsured"
          @clear="clearAdditionalInsured"
          @toast="$emit('toast', $event)"
        />
      </template>
    </form-section>
  </validation-observer>
</template>

<script>
import { cloneDeep } from 'lodash';

import { validate } from 'vee-validate';

import {
  INSURED_ADDITIONAL,
} from '@/payloadTemplates/home';

import FormAdditionalInsured from '@/components/home/FormAdditionalInsured/FormAdditionalInsured';

import { getAdditionalInsuredRules } from '@/prevalidation/home';

export default {
  name: 'question-additional-insured',
  inject: [
    'constants',
    'inputCharsLimiters',
    'helpers',
    'config',
  ],
  components: {
    FormAdditionalInsured,
  },
  props: {
    productName: {
      type: String,
      required: true,
    },
    stepId: {
      type: String,
      required: true,
    },
    formObserver: {
      type: Object,
      required: true,
    },
    formData: {
      type: Object,
      required: true,
    },
    stepData: {
      type: [Object, Array],
      required: true,
    },
    showConfirmModal: {
      type: Function,
      required: true,
    },
  },
  data () {
    return {
      additionalInsured: null,
      additionalInsuredBackup: null,

      isAdditionalInsuredTracked: false,

      additionalItemsValidity: null,

      scrollTarget: 'joint-insured',
    };
  },
  created () {
    if (this.hasAdditionalInsured) {
      this.additionalInsuredBackup = cloneDeep(this.stepData[1]);
    }

    this.$watch('insured.jointInsured', val => {
      if (val) {
        if (this.hasAdditionalInsured) return;

        if (this.additionalInsuredBackup) {
          this.$emit('save-additional',
            { insured: this.additionalInsuredBackup, isUpdate: false });
        } else {
          this.additionalInsured = cloneDeep(INSURED_ADDITIONAL);
        }

        if (!this.isAdditionalInsuredTracked) {
          this.isAdditionalInsuredTracked = true;
          this.$emit('tracking');
        }
      } else {
        if (!this.hasAdditionalInsured) return;

        this.showConfirmModal({
          text: this.additionalInsuredBackup ? 'Clicking Yes will remove this person from the quote' : '',
          yesButton: 'Yes',
          noButton: 'No',
          yesCallback: () => {
            this.resetAdditionalInsured();
            this.$scrollTo(`#${this.scrollTarget}`);
          },
          noCallback: () => {
            this.additionalInsuredBackup = cloneDeep(this.stepData[1]);
            this.insured.jointInsured = true;
          },
        });
      }
    });

    this.$watch('additionalInsuredList',
      async additionalInsuredList => {
        if (!additionalInsuredList || additionalInsuredList.length === 0) {
          this.additionalItemsValidity = null;
          return;
        }

        const results = await Promise.all(additionalInsuredList.map(item => validate(item, {
          subform: {
            template: INSURED_ADDITIONAL,
            rules: getAdditionalInsuredRules(item),
          },
        })));
        this.additionalItemsValidity = results.slice();
      },
      {
        deep: true,
        immediate: true,
      });
  },
  computed: {
    insured: {
      get () {
        return this.stepData[0];
      },
      set (value) {
        this.$emit('input', { jointInsured: value });
      },
    },

    isAllAdditionalInsuredItemsValid () {
      return Array.isArray(this.additionalItemsValidity)
        ? !this.additionalItemsValidity.some(({ valid }) => !valid) : true;
    },

    hasAdditionalInsured () { return !!this.stepData[1]; },

    additionalInsuredList () {
      const insured = this.stepData.slice();
      if (insured.length > 1) {
        insured.shift();
        return insured;
      }
      return [];
    },

    isRelationshipProposerValid () {
      // additional insured can't be husband or wife if main insured isn't married
      if (this.hasAdditionalInsured &&
        ['H', 'W'].includes(this.stepData[1].additionalRelationshipProposer) &&
        !['M', 'V'].includes(this.insured.maritalStatus)) {
        return false;
      }

      return true;
    },
  },
  methods: {
    resetAdditionalInsured () {
      this.$emit('reset-additional');
    },
    async clearAdditionalInsured () {
      if (!this.hasAdditionalInsured) {
        this.additionalInsured = cloneDeep(INSURED_ADDITIONAL);
        await this.$nextTick();
        this.insured.jointInsured = false;
        this.$scrollTo(`#${this.scrollTarget}`);
      } else {
        this.additionalInsured = null;
        await this.$nextTick();
        this.$scrollTo(`#${this.scrollTarget}`);
      }
    },
    async saveAdditionalInsured (insured) {
      this.$emit('save-additional',
        { insured, isUpdate: this.hasAdditionalInsured });

      this.additionalInsuredBackup = insured;
      this.additionalInsured = null;
      await this.$nextTick();
      this.$scrollTo(`#${this.scrollTarget}`);
    },
    removeAdditionalInsured () {
      this.additionalInsuredBackup = null;
      this.insured.jointInsured = false;
    },
    async changeAdditionalInsured () {
      this.additionalInsured = cloneDeep(this.stepData[1]);
      await this.$nextTick();
      this.$scrollTo('#additional-insured-edit');
    },
  },

};
</script>
